import {
  Box,
  Container,
  FormControl,
  Link,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import Title from "../../components/Pages/Title";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import React, { useEffect, useState } from "react";
import { quoteApi, tokenApi } from "../../openApi";
import { Quote, Token } from "../../openApi/api";
import Pagination from "../../components/Pagination";
import { useUpcxChain } from "../../hook/useUpcxChain";
import { QuotesService, TokensService } from "../../backend";

const TopToken = () => {
  const { selectedShard } = useUpcxChain();

  const [tokens, setTokens] = useState<Token[]>([]);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const [quotes, setQuotes] = useState<Quote[]>([]);

  const handleChange = (event: SelectChangeEvent) => {
    setLimit(parseInt(event.target.value));
    setPage(0);
  };

  const getTokenList = async () => {
    const result = await TokensService.getTokens(limit, page);
    setTokens(
      result.tokens?.filter((x) => x.verified && x.name !== "UPC") ?? []
    );
    setTotal(result.total ?? 0);
  };

  useEffect(() => {
    if (!selectedShard) {
      return;
    }

    getTokenList();
    // eslint-disable-next-line
  }, [limit, page, selectedShard]);

  useEffect(() => {
    if (!selectedShard) {
      return;
    }

    QuotesService.getAllQuotes().then((result) => {
      setQuotes(result);
    });
  }, [selectedShard]);

  return (
    <>
      <Title title="Top Tokens" smallTitle="" />

      <Container className="container-xxl pt-5 pb-12">
        <Box className="card">
          <Box className="card-body">
            <Box className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <Box>
                <span className="text-dark content-center gap-1">
                  A total of {total.toLocaleString()} Token Contracts found
                </span>
              </Box>
            </Box>
          </Box>

          <TableContainer component={Paper} className="box-shadow-0">
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="left" width={1}>
                    #
                  </TableCell>
                  <TableCell>Token</TableCell>
                  <TableCell align="left">
                    <Tooltip title="Click for descending sort" placement="top">
                      <Link>Price</Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="left">
                    <Tooltip title="Click for descending sort" placement="top">
                      <Link>Change (%)</Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="left">
                    <Tooltip title="Click for descending sort" placement="top">
                      <Link>Volume</Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="left">
                    <Tooltip title="Click for descending sort" placement="top">
                      <Link>Circulating Market Cap</Link>
                    </Tooltip>
                    <Tooltip
                      title="Calculated by multiplying the number of tokens in circulating supply across all chains with the current market price per token."
                      placement="top"
                    >
                      <HelpOutlineIcon className="text-icon text-muted" />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="left">
                    On-Chain Market Cap
                    <Tooltip
                      title="Calculated by multiplying the number of tokens in circulating supply across all chains with the current market price per token."
                      placement="top"
                    >
                      <HelpOutlineIcon className="text-icon text-muted" />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="left">
                    <Tooltip title="Click for descending sort" placement="top">
                      <Link>Holders</Link>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {tokens.map((data, index) => {
                  const currentQuote = quotes.find(
                    (x) => x.token === data.name && x.base === "UPC"
                  );
                  return (
                    <TableRow>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Link
                          className="d-flex align-items-center gap-1 link-dark"
                          href="/token/0xdac17f958d2ee523a2206206994597c13d831ec7"
                        >
                          <img
                            src="/assets/img/logo_black.png"
                            width="24"
                            height="24"
                            className="rounded-circle"
                            alt="Token Logo"
                          />
                          <Box className="d-flex gap-1">
                            <Box className="hash-tag text-truncate fw-medium">
                              {data.name}
                            </Box>
                          </Box>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Box className="d-inline">
                          ${currentQuote?.marketCapPrice?.toFixed(2)}
                        </Box>
                        <Box className="text-muted small">
                          {currentQuote?.marketCapPrice?.toFixed(5)} UPC
                        </Box>
                      </TableCell>
                      <TableCell>
                        <span className="text-success text-nowrap">
                          <ArrowDropUpIcon className="text-icon me-1" />
                          {currentQuote?.marketCapRisePercentage?.toFixed(2)}%
                        </span>
                      </TableCell>

                      <TableCell>
                        $
                        {(
                          data.totalSupply * (currentQuote?.marketCapPrice ?? 0)
                        ).toLocaleString()}
                      </TableCell>

                      <TableCell>
                        ${currentQuote?.marketCapVolumn.toLocaleString()}
                      </TableCell>
                      <TableCell>$0.00 </TableCell>
                      <TableCell>
                        <Box>5,058,533</Box>

                        <Tooltip
                          title="A 0.025% increase in Token holders from previous day count of 5057257"
                          placement="top"
                        >
                          <Box className="text-success small d-inline">
                            0.025%
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box className="card-body">
            <Box className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <Box
                id="ContentPlaceHolder1_pageRecords"
                className="d-flex align-items-center gap-2 text-nowrap text-muted"
              >
                Show:
                <FormControl sx={{ m: 1, minWidth: 70 }}>
                  <Select
                    value={limit.toString()}
                    onChange={handleChange}
                    displayEmpty
                    size="small"
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    <MenuItem
                      value={10}
                      sx={{
                        fontSize: "12px;",
                      }}
                    >
                      10
                    </MenuItem>
                    <MenuItem
                      sx={{
                        fontSize: "12px;",
                      }}
                      value={25}
                    >
                      25
                    </MenuItem>
                    <MenuItem
                      sx={{
                        fontSize: "12px;",
                      }}
                      value={50}
                    >
                      50
                    </MenuItem>
                    <MenuItem
                      sx={{
                        fontSize: "12px;",
                      }}
                      value={100}
                    >
                      100
                    </MenuItem>
                  </Select>
                </FormControl>
                Records
              </Box>
              <Box className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <Pagination
                  limit={limit}
                  page={page}
                  setPage={setPage}
                  total={total}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default TopToken;
